/**
 * Guest Request File Search API
 * 
 * Wrapper for searching guest request files using Elasticsearch
 */
import { v1Api } from '../../../api/generatedClient'
import type { APIGuestRequestFileSearchResponse } from '../../../generated-types/models/APIGuestRequestFileSearchResponse'
import type { APIGuestRequestFileSearchResult } from '../../../generated-types/models/APIGuestRequestFileSearchResult'
import type { V1SearchGuestRequestsGuestRequestFilesRetrieveRequest } from '../../../generated-types/apis/V1Api'
import { createFileInfo } from '../../../utils/functions'
import type { FileInfo } from '../../../utils/types'

export interface GuestRequestFileSearchFilters {
  q: string
  guestRequestId?: number
  fileType?: string
  fileName?: string
  createdAt?: string // YYYY-MM-DD
  fileSize?: string // File size (e.g., '10287', '10.05', '10.05 KB', '1.5 MB')
  fields?: string[]
  size?: number
  from?: number
}

export interface GuestRequestFileSearchResponse {
  total: number
  max_score: number | null
  results: FileInfo[]
  page: {
    from: number
    size: number
    has_more: boolean
  }
}

function toGuestRequestFileSearchRequest(
  filters: GuestRequestFileSearchFilters
): V1SearchGuestRequestsGuestRequestFilesRetrieveRequest {
  if (!filters.q || filters.q.trim() === '') {
    throw new Error('Search query (q) is required')
  }

  return {
    q: filters.q,
    fields: filters.fields && filters.fields.length > 0 ? filters.fields.join(',') : undefined,
    size: filters.size,
    from: filters.from,
    guestRequestId: filters.guestRequestId,
    fileType: filters.fileType,
    fileName: filters.fileName,
    createdAt: filters.createdAt ? new Date(filters.createdAt) : undefined,
    fileSize: filters.fileSize,
  }
}

/**
 * Search for guest request files using Elasticsearch
 * 
 * @param filters - Search filters
 * @returns Guest request file search response with results and pagination info
 */
export async function searchGuestRequestFiles(
  filters: GuestRequestFileSearchFilters
): Promise<GuestRequestFileSearchResponse> {
  const response: APIGuestRequestFileSearchResponse = await v1Api.v1SearchGuestRequestsGuestRequestFilesRetrieve(
    toGuestRequestFileSearchRequest(filters)
  )

  // Map results to FileInfo
  // Note: Guest request files use createdAt instead of storedAt
  const fileInfos = response.results.map((result) => {
    const fileInfo: Record<string, unknown> = {
      self: `/v1/guest-request/guest-request/${result.guestRequestId ?? 'unknown'}/files/${result.id}/`,
      name: result.fileName ?? '',
      key: result.key ?? '',
      created_at: result.createdAt?.toISOString(), // Guest request files use created_at
      last_modified_at: result.lastModifiedAt?.toISOString(),
    }

    if (result.guestRequestId) {
      fileInfo.guest_request = `/v1/guest-request/guest-request/${result.guestRequestId}/`
    }

    return createFileInfo(fileInfo)
  })

  return {
    total: response.total,
    max_score: response.maxScore,
    results: fileInfos,
    page: {
      from: response.page.fromValue,
      size: response.page.size,
      has_more: response.page.hasMore,
    },
  }
}
